import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom'; // Necessário para ler a URL
import { PublicProductAPI } from '../../../services/PublicProductAPI.js';
import { adminCategoryAPI } from '../../../admin/Handlers/categoryAPI.js';
import ProductCard from './Card/ProductCard'; 
import styles from './PublicProductPage.module.css';

function PublicProductPage() {
    const location = useLocation(); // Hook para acessar a URL atual
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const controller = new AbortController();

        const fetchData = async () => {
            try {
                // Busca produtos e categorias simultaneamente
                const [productData, categoryData] = await Promise.all([
                    PublicProductAPI.list_products({ signal: controller.signal }),
                    adminCategoryAPI.list()
                ]);
                
                setProducts(productData || []);
                setCategories(categoryData || []);

                // Lógica de captura do parâmetro ?category=ID da URL
                const params = new URLSearchParams(location.search);
                const catFromUrl = params.get('category');
                
                if (catFromUrl) {
                    setSelectedCategory(catFromUrl);
                }

            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error("Erro ao carregar produtos:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        return () => controller.abort();
    }, [location.search]); // Recarrega se a URL mudar (ex: clicar em outra categoria no menu)

    // Lógica de Filtragem (Texto + Categoria)
    const filteredProducts = products.filter(product => {
        const matchesName = product.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === '' || String(product.category_id) === String(selectedCategory);
        return matchesName && matchesCategory;
    });

    return (
        <div className={styles.pageContainer}>
            <header className={styles.pageHeader}>
                <div className={styles.titleArea}>
                    <h2 className={styles.title}>Nossa Vitrine<span>.</span></h2>
                    <p className={styles.subtitle}>
                        {searchTerm || selectedCategory 
                            ? `${filteredProducts.length} resultados encontrados` 
                            : `${products.length} itens em estoque`}
                    </p>
                </div>

                <div className={styles.controlsContainer}>
                    {/* Select de Categorias */}
                    <div className={styles.filterWrapper}>
                        <select 
                            className={styles.categorySelect}
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="">Todas as Categorias</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Barra de Busca */}
                    <div className={styles.searchWrapper}>
                        <span className={styles.searchIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        </span>
                        <input 
                            type="text" 
                            placeholder="Buscar produto..." 
                            className={styles.searchInput}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className={styles.clearBtn} onClick={() => setSearchTerm('')}>&times;</button>
                        )}
                    </div>
                </div>
            </header>

            {loading ? (
                <div className={styles.statusArea}>
                    <div className={styles.spinner}></div>
                    <p>Sincronizando vitrine...</p>
                </div>
            ) : (
                <main className={styles.productGrid}>
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                    
                    {filteredProducts.length === 0 && (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>📂</div>
                            <h3>Nenhum produto encontrado</h3>
                            <p>Tente ajustar os filtros ou limpar a busca.</p>
                            <button 
                                onClick={() => {setSearchTerm(''); setSelectedCategory('');}} 
                                className={styles.resetBtn}
                            >
                                Limpar Tudo
                            </button>
                        </div>
                    )}
                </main>
            )}
        </div>
    );
}

export default PublicProductPage;